"use client";
import React, {
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Combobox, Transition } from "@headlessui/react";
import Fuse from "fuse.js";
import { useAutoClose } from "~/hooks/useAutoClose";
import { cn } from "~/utils/fontsConfig";
import { If } from "../Conditional";
import { BASE_INPUT_STYLES } from "./BASE_STYLE_DEFINITIONS";
import { FaCaretDown } from "react-icons/fa";

interface ThemedAutocompleteProps<T> {
    setQuery?: (value: string) => void;
    data?: T[];
    onSelected?: (value?: T) => void;
    selected?: T;
    idKey: keyof T;
    valueKey: (keyof T)[];
    lastOption?: ReactNode;
    placeholder?: string;
    suffix?: ReactNode;
    displayValue?: (value: T) => string;
    className?: string;
    disabled?: boolean;
    showDropdownIndicator?: boolean;
}

// Documentation: https://headlessui.com/react/combobox
const ThemedAutocomplete = <T,>({
    selected,
    onSelected,
    setQuery,
    data,
    idKey,
    valueKey,
    lastOption,
    placeholder,
    suffix,
    displayValue,
    className,
    disabled,
    showDropdownIndicator,
}: ThemedAutocompleteProps<T>) => {
    const [internalQuery, setInternalQuery] = useState<string>("");
    const ref = useRef<HTMLInputElement>(null);
    const [clickOpen, setClickOpen] = useAutoClose(false, ref);

    const fuse = useMemo(() => {
        return new Fuse(data || [], {
            keys: valueKey as string[],
            threshold: 0.3,
        });
    }, [data]);

    const [filteredResults, setFilteredResults] = useState(data || []);

    // Maybe rethink how to do this debounced search once we need this Autocomplete in other places
    const debouncedSearch = useCallback(() => {
        const debounceTimeout = setTimeout(() => {
            const results = internalQuery
                ? fuse.search(internalQuery).map(({ item }) => item)
                : data || [];

            setFilteredResults(results);
        }, 300);

        return () => clearTimeout(debounceTimeout);
    }, [data, internalQuery, fuse]);

    useEffect(() => {
        return debouncedSearch();
    }, [internalQuery, debouncedSearch]);

    if (!valueKey[0]) throw new Error("valueKey must have at least one key");

    return (
        <section className="relative w-inherit">
            <Combobox
                value={selected}
                onChange={(e) => {
                    onSelected && onSelected(e);
                }}
                disabled={disabled}
            >
                {({ open }) => (
                    <>
                        <div className="relative w-inherit">
                            <Combobox.Input
                                displayValue={
                                    displayValue
                                        ? displayValue
                                        : (e: T) => e[valueKey[0]!] as string
                                }
                                onClick={() => setClickOpen(true)}
                                ref={ref}
                                onChange={(event) => {
                                    setQuery?.(event.target.value);
                                    setInternalQuery(event.target.value);
                                }}
                                placeholder={placeholder || ""}
                                className={cn(
                                    BASE_INPUT_STYLES,
                                    "md:w-60",
                                    className,
                                )}
                                autoComplete="off"
                            />
                            {showDropdownIndicator && (
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                    <FaCaretDown
                                        className="h-5 w-5 text-gray-400"
                                        aria-hidden="true"
                                    />
                                </div>
                            )}
                        </div>

                        {(open || clickOpen) && (
                            <Combobox.Options
                                static
                                className={cn(
                                    "accented-scrollbar-thumb absolute top-12 z-10 max-h-60 w-full overflow-y-auto rounded-lg border-2 border-v3-darker-gray bg-v3-neutral p-2 font-faktum shadow-lg",
                                    className,
                                )}
                            >
                                {(filteredResults ?? []).map((value) => (
                                    <Combobox.Option
                                        className={cn(
                                            `select-none rounded-lg p-2 font-faktum hover:bg-[#373A41] hover:underline`,
                                            className,
                                        )}
                                        key={(value as any)[idKey]}
                                        value={value}
                                    >
                                        {/* Since this value is at the end of the type chain, it doesn't matter we cast it to any at this point */}
                                        {displayValue
                                            ? displayValue(value)
                                            : valueKey
                                                  .map(
                                                      (key) =>
                                                          (value as any)[key],
                                                  )
                                                  .join(" ")}
                                    </Combobox.Option>
                                ))}
                                {lastOption ?? <></>}
                            </Combobox.Options>
                        )}

                        <If condition={!!suffix}>{suffix}</If>
                    </>
                )}
            </Combobox>
        </section>
    );
};

export default ThemedAutocomplete;
